import { StageDef } from '../automationMachineInterfaces'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  getSettingRowWithToggle,
  getSettingToggleValue,
  setSettingToggleValue,
} from './fbSettingsUtils'
import { howPeopleFindYouUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import SettingNotFoundException from '../exception/settingNotFoundException'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Setting property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'On' : 'Off'))
const getIsFulfilledFn = () =>
  getIsFulfilledFnFromKey(
    FacebookSettingKey.SEARCH_ENGINE_LINKING,
    (value) => !value,
  )
const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

export const SEARCH_ENGINE_LINKING_LABEL_US =
  'Do you want search engines outside of Facebook to link to your profile?'
export const SEARCH_ENGINE_LINKING_LABEL_UK =
  'Do you want search engines outside Facebook to link to your profile?'

// Gather stage

enum StageKey {
  INIT_SETTING_SEARCH_ENGINE_LINKING = 'INIT_SETTING_SEARCH_ENGINE_LINKING',
  GATHER_SETTING_SEARCH_ENGINE_LINKING = 'GATHER_SETTING_SEARCH_ENGINE_LINKING',
  UPDATE_SETTING_SEARCH_ENGINE_LINKING = 'UPDATE_SETTING_SEARCH_ENGINE_LINKING',
  UNDO_SETTING_SEARCH_ENGINE_LINKING = 'UNDO_SETTING_SEARCH_ENGINE_LINKING',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_SEARCH_ENGINE_LINKING,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredAutomationErrors, updateStoredInitialValues } =
        userContext

      try {
        const searchEngineLinkingRow = await Promise.any([
          getSettingRowWithToggle(SEARCH_ENGINE_LINKING_LABEL_US),
          getSettingRowWithToggle(SEARCH_ENGINE_LINKING_LABEL_UK),
        ]).catch((error) => {
          throw new SettingNotFoundException(error.message)
        })

        const searchEngineOn = await getSettingToggleValue(
          searchEngineLinkingRow,
        )

        updateStoredInitialValues(
          {
            [FacebookSettingKey.SEARCH_ENGINE_LINKING]: searchEngineOn,
          },
          FBRecommendationKey.FB_SEARCH_ENGINE,
        )
      } catch (error) {
        await updateStoredAutomationErrors(
          {
            [FacebookSettingKey.SEARCH_ENGINE_LINKING]: {
              gatherError: { message: error.message, name: error.name },
            },
          },
          FBRecommendationKey.FB_SEARCH_ENGINE,
        )
        await updateStoredInitialValues(
          {
            [FacebookSettingKey.SEARCH_ENGINE_LINKING]: null,
          },
          FBRecommendationKey.FB_SEARCH_ENGINE,
        )
      }
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_SEARCH_ENGINE_LINKING,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      if (settingsSelected[FacebookSettingKey.SEARCH_ENGINE_LINKING]) {
        const searchEngineLinkingRow = await Promise.any([
          getSettingRowWithToggle(SEARCH_ENGINE_LINKING_LABEL_US),
          getSettingRowWithToggle(SEARCH_ENGINE_LINKING_LABEL_UK),
        ])
        const searchEngineLinkingOn = await getSettingToggleValue(
          searchEngineLinkingRow,
        )
        if (searchEngineLinkingOn && !isDry) {
          await setSettingToggleValue(searchEngineLinkingRow, false)
        }
      }

      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_SEARCH_ENGINE_LINKING,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      const initialSettingValue =
        initialValues[FacebookSettingKey.SEARCH_ENGINE_LINKING]

      if (
        settingsSelected[FacebookSettingKey.SEARCH_ENGINE_LINKING] &&
        initialSettingValue
      ) {
        const searchEngineLinkingRow = await Promise.any([
          getSettingRowWithToggle(SEARCH_ENGINE_LINKING_LABEL_US),
          getSettingRowWithToggle(SEARCH_ENGINE_LINKING_LABEL_UK),
        ])

        const searchEngineLinkingOn = await getSettingToggleValue(
          searchEngineLinkingRow,
        )
        if (searchEngineLinkingOn !== initialSettingValue && !isDry) {
          await setSettingToggleValue(
            searchEngineLinkingRow,
            initialSettingValue,
          )
        }
      }

      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
